import { type FC } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'

import { ProvideAuth } from '@lib/hooks'
import { AuthenticatedRoute, PageViewEvents, ScrollToTop, Spinner } from '@lib/components'
import { ActiveCampaign, CheckoutPlanProvider, PaymentContainer, MTDTracking, defaultVariants } from '@shamaazi/mytennights'

import { CookieBanner } from '~/components'
import { Contact, CookiePreferences, Home, Payment, Preferences, Schedule, ScheduleList, SchedulePayment } from '~/pages'
import { Confirmation } from '~/pages/Share'
import PrivacyPolicy from '~/pages/PrivacyPolicy'
import TermsAndConditions from './pages/TermsAndConditions'
import FAQs from './pages/FAQs'
import { FooterLayout } from '~/components/Footer'
import { MixpanelInstance, SplittestingStore } from '@lib/services'
import { Navigation } from '~/navigation/navigation'
import ShareOptions from './pages/ShareOptions'
import { LoginPage } from './pages/Login'
import { DonationSplit } from './pages/DonationSplit'
import { Extras } from './pages/Extras'
import { Qurbani } from './pages/Qurbani'
import Charity from './pages/Charity'
import { configure } from 'mobx'
import { observer } from 'mobx-react'
import { SelectCharity } from './pages/SelectCharity'

configure({
  enforceActions: 'never'
})

const queryClient = new QueryClient()

const tracking = new MTDTracking()
tracking.initialize()

interface AppProps {
  readonly splittestingStore: SplittestingStore
}

const splitTestingStore = new SplittestingStore(defaultVariants)
void splitTestingStore.load()

const App: FC<AppProps> = observer(({ splittestingStore }) => {
  return <>
  {splittestingStore.isLoading &&
    <div className="flex justify-center mt-20"><Spinner className="text-mtd-blue" /></div>}
  {!splittestingStore.isLoading && !splittestingStore.error &&
      <ProvideAuth campaignQueryParameter={ActiveCampaign.myTenDays} onSetUser={(u) => MixpanelInstance.tryIdentify(u?.email)}>
    <QueryClientProvider client={queryClient}>
      <CheckoutPlanProvider>
        <Router>
          <ScrollToTop />
          <PageViewEvents tracking={tracking} trackable={tracking.isTrackableUrl} />
          <FooterLayout>
            <Routes>
              <Route path={Navigation.home()} element={<Home nextPage={Navigation.selectCharityCauses()} />} />

              <Route path={Navigation.selectCharityCauses()} element={
                <SelectCharity nextPage={Navigation.donationSplit()} />
              }/>

              <Route path={Navigation.donationSplit()} element={
                <DonationSplit />
              }/>

              <Route path={Navigation.qurbani()} element={
                <Qurbani nextPage={Navigation.extras()} />
              } />

              <Route path={Navigation.extras()} element={
                <Extras nextPage={Navigation.payment()} />
              } />

              <Route path={Navigation.payment()} element={
                <>
                <PaymentContainer campaign="MyTenDays">
                  <Payment/>
                </PaymentContainer>
                </>
              }/>

              <Route path={Navigation.confirmation()} element={<Confirmation />} />

              <Route path="/privacy-policy" element={<PrivacyPolicy />}/>

              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

              <Route path="/account/schedules" element={<AuthenticatedRoute loginComponent={<LoginPage/>} >
                <ScheduleList />
              </AuthenticatedRoute>} />
              <Route path="/account/schedule/:scheduleID/payment/:number" element={<AuthenticatedRoute loginComponent={<LoginPage/>}>
                <PaymentContainer campaign="MyTenDays">
                  <SchedulePayment />
                </PaymentContainer>
              </AuthenticatedRoute>} />
              <Route path="/account/schedule/:scheduleID" element={<AuthenticatedRoute loginComponent={<LoginPage/>}>
                <Schedule />
              </AuthenticatedRoute>} />
              <Route path="/account/preferences" element={<AuthenticatedRoute loginComponent={<LoginPage/>}>
                <Preferences />
              </AuthenticatedRoute>} />
              <Route path="/cookie-preferences" element={<CookiePreferences />} />

              <Route path="/faqs" element={<FAQs />} />

              <Route path="/contact" element={<Contact />}/>

              {<Route path="/:charityID" element={<Charity nextPage={Navigation.donationSplit()} />} />}

              <Route path="/share/:shareCode" element={<ShareOptions />} />

              {<Route path="/link/:chainCode" element={<Home nextPage={Navigation.selectCharityCauses()} />} />}

              <Route element={<h1 className="heading-xl">This page could not be found</h1>} />

            </Routes>
          </FooterLayout>
          <CookieBanner />
        </Router>
        </CheckoutPlanProvider>
        </QueryClientProvider>
      </ProvideAuth>
    }
  </>
})

const AppContainer: FC = () => {
  return <App splittestingStore={splitTestingStore}/>
}

export default AppContainer
